import { configureStore, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi, weddingApi } from '../services/api';

export const verifyEmailThunk = createAsyncThunk('auth/verifyEmail', async (token, { rejectWithValue }) => {
  try {
    const { data } = await authApi.verifyEmail(token);
    localStorage.setItem('token', data.token);
    return data;
  } catch (e) {
    const message = e.response?.data?.message || e.message || 'Verification failed';
    return rejectWithValue(message);
  }
});

// ─── Auth Slice ────────────────────────────────────────────────────────────
export const loginThunk = createAsyncThunk('auth/login', async (creds, { rejectWithValue }) => {
  try {
    const { data } = await authApi.login(creds);
    localStorage.setItem('token', data.token);
    return data;
  } catch (e) {
    const message = e.response?.data?.message || e.message || 'Login failed';
    return rejectWithValue(message);
  }
});

export const registerThunk = createAsyncThunk('auth/register', async (creds, { rejectWithValue }) => {
  try {
    const { data } = await authApi.register(creds);
    localStorage.setItem('token', data.token);
    return data;
  } catch (e) {
    const message = e.response?.data?.message || e.message || 'Registration failed';
    return rejectWithValue(message);
  }
});

export const getMeThunk = createAsyncThunk('auth/getMe', async (_, { rejectWithValue }) => {
  try {
    const { data } = await authApi.getMe();
    return data;
  } catch (e) {
    // Only reject with 401 — network errors should NOT log the user out
    const status = e.response?.status;
    if (status === 401) {
      return rejectWithValue({ status: 401, message: 'Session expired' });
    }
    // Network error or server down — keep existing token, just mark initialized
    return rejectWithValue({ status: null, message: e.message });
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('token'),
    loading: false,
    error: null,
    initialized: false,
  },
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.error = null;
      state.initialized = true;
      localStorage.removeItem('token');
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (b) => {
    // Login
    b.addCase(loginThunk.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(loginThunk.fulfilled, (s, a) => {
      s.loading = false;
      s.user = a.payload.user;
      s.token = a.payload.token;
      s.initialized = true;
      s.error = null;
    });
    b.addCase(loginThunk.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload;
      s.initialized = true;
    });

    // Register
    b.addCase(registerThunk.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(registerThunk.fulfilled, (s, a) => {
      s.loading = false;
      s.user = a.payload.user;
      s.token = a.payload.token;
      s.initialized = true;
      s.error = null;
    });
    b.addCase(registerThunk.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload;
      s.initialized = true;
    });

    // Email verification link
    b.addCase(verifyEmailThunk.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(verifyEmailThunk.fulfilled, (s, a) => {
      s.loading = false;
      s.user = a.payload.user;
      s.token = a.payload.token;
      s.initialized = true;
      s.error = null;
    });
    b.addCase(verifyEmailThunk.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload;
      s.initialized = true;
    });

    // Get current user (on app boot)
    b.addCase(getMeThunk.pending, (s) => { s.loading = true; });
    b.addCase(getMeThunk.fulfilled, (s, a) => {
      s.loading = false;
      s.user = a.payload;
      s.initialized = true;
    });
    b.addCase(getMeThunk.rejected, (s, a) => {
      s.loading = false;
      s.initialized = true;
      // ONLY clear token on explicit 401 — NOT on network errors
      if (a.payload?.status === 401) {
        s.token = null;
        s.user = null;
        localStorage.removeItem('token');
      }
      // If server is just down, keep user logged in with existing token
    });
  },
});

// ─── Weddings Slice ────────────────────────────────────────────────────────
export const fetchWeddings = createAsyncThunk('weddings/fetch', async (_, { rejectWithValue }) => {
  try {
    const { data } = await weddingApi.getAll();
    return data;
  } catch (e) {
    const status = e.response?.status;
    const message = e.response?.data?.message || 'Failed to load weddings';
    return rejectWithValue({ status, message });
  }
});

export const createWedding = createAsyncThunk('weddings/create', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await weddingApi.create(payload);
    return data;
  } catch (e) {
    const d = e.response?.data;
    if (d && typeof d === 'object') return rejectWithValue(d);
    return rejectWithValue({ message: e.response?.data?.message || 'Failed to create wedding' });
  }
});

const weddingsSlice = createSlice({
  name: 'weddings',
  initialState: { list: [], loading: false, error: null },
  reducers: {
    removeWedding(state, action) {
      state.list = state.list.filter((w) => w._id !== action.payload);
    },
    addWedding(state, action) {
      state.list.unshift(action.payload);
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchWeddings.pending, (s) => { s.loading = true; s.error = null; });
    b.addCase(fetchWeddings.fulfilled, (s, a) => { s.loading = false; s.list = a.payload; });
    b.addCase(fetchWeddings.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
    b.addCase(createWedding.fulfilled, (s, a) => { s.list.unshift(a.payload); });
  },
});

export const { logout, clearError } = authSlice.actions;
export const { removeWedding, addWedding } = weddingsSlice.actions;

export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    weddings: weddingsSlice.reducer,
  },
});
